/**
 * HighImpactApprovalGate — Phase 5 (C-160 / lifecycle-redesign invariant 6).
 *
 * A confirm modal the checker must pass through before APPLYING a high-impact
 * change request (a cascade touching >= 1 linked record, or a Promote that
 * allocates a permanent client number). Reject never opens this gate —
 * rejecting a high-impact CR stays one-click (Pedro 2026-06-16:
 * "labelled-but-one-click reject"); the CR is still flagged high-impact on
 * the checker side via the same `impact` summary (banner + list badge).
 *
 * §3j: the banner names the operation and the affected count; the confirm
 * button restates the blast radius ("Approve & apply (affects N records)").
 * The caller passes the relevant plan preview (cascade / policy) as children
 * so the checker re-sees exactly what will change inside the gate.
 */
import { Alert, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import type { ImpactSummary } from '../api/types';

interface HighImpactApprovalGateProps {
  opened: boolean;
  impact: ImpactSummary;
  /** Approve mutation in flight — disables the buttons + spins the confirm. */
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Optional plan preview (cascade / policy) rendered read-only in the gate. */
  children?: ReactNode;
}

export function HighImpactApprovalGate({
  opened, impact, loading, onConfirm, onCancel, children,
}: HighImpactApprovalGateProps) {
  const n = impact.affected_total;
  const records = n === 1 ? '1 record' : `${n} records`;
  const action = impact.op_label ?? 'this change';

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title="Confirm high-impact approval"
      size="lg"
      centered
    >
      <Stack>
        <Alert
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title="High-impact change"
        >
          You are about to approve and apply <strong>{action}</strong>.{' '}
          {n > 0 ? (
            <>
              This cascades to <strong>{records}</strong> linked to this entity.
              Applying is immediate and changes the live records.
            </>
          ) : (
            <>
              This allocates a permanent client number and cannot be undone by
              a later edit. Applying is immediate.
            </>
          )}
        </Alert>

        {children}

        <Text size="xs" c="dimmed">
          Rejecting does not require confirmation — only applying a high-impact
          change does.
        </Text>

        <Group justify="flex-end">
          <Button
            variant="default"
            leftSection={<IconX size={14} />}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="green"
            leftSection={<IconCheck size={14} />}
            loading={loading}
            onClick={onConfirm}
          >
            {n > 0 ? `Approve & apply (affects ${records})` : 'Approve & apply'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
